import { getNamespacedResource } from "../util";
import { convertValueToString } from "Util/util";
import { useProjectLockStore } from "Projects/projectLockStore.js";

const COMMIT_DATA = "commitProjectData";
const COMMIT_MULTIDATA = "commitProjectMultiData";
const COMMIT_MULTIDATA_ITEM = "commitMultiDataItem";
const COMMIT_EVALUATED_FIELD = "commitEvaluatedField";
const COMMIT_ATTACHMENT = "commitAttachment";
const COMMIT_ENVELOPE = "commitEnvelope";
const PROJECT_NAMESPACE = "project";

const DATA_FIELD_VALUE_FROM_ID = "dataFieldValueFromId";
const DATA_FIELD_MULTIVALUE_FROM_ID = "dataFieldMultiValueFromId";
const ATTACHMENT_FROM_ID = "attachmentFromId";
const GET_ATTACHMENT_DEFINITION_ID_FROM_ATTACHMENT_ID = "getAttachmentDefinitionIdFromAttachmentId";
const GET_ENVELOPE_FROM_DEFINITION_ID = "getEnvelopeFromDefinitionId";
const GET_ENVELOPE_FROM_ID = "getEnvelopeFromId";
const EVALUATED_FIELD_FROM_ID = "evaluatedFieldFromId";
const IS_ATTACHMENT_ID_PART_OF_ENVELOPE = "isAttachmentIdPartOfEnvelope";

const HIDE_ATTACHMENT_BY_DEFINITION_ID = "hideAttachmentByDefinitionId";
const HIDE_ENVELOPE = "hideEnvelope";
const REMOVE_ENVELOPE = "removeEnvelope";

const PROJECT_TYPES = {
    getters: {
        [DATA_FIELD_VALUE_FROM_ID]: getNamespacedResource(PROJECT_NAMESPACE, DATA_FIELD_VALUE_FROM_ID),
        [DATA_FIELD_MULTIVALUE_FROM_ID]: getNamespacedResource(PROJECT_NAMESPACE, DATA_FIELD_MULTIVALUE_FROM_ID),
        [ATTACHMENT_FROM_ID]: getNamespacedResource(PROJECT_NAMESPACE, ATTACHMENT_FROM_ID),
        [GET_ATTACHMENT_DEFINITION_ID_FROM_ATTACHMENT_ID]: getNamespacedResource(
            PROJECT_NAMESPACE,
            GET_ATTACHMENT_DEFINITION_ID_FROM_ATTACHMENT_ID
        ),
        [GET_ENVELOPE_FROM_DEFINITION_ID]: getNamespacedResource(PROJECT_NAMESPACE, GET_ENVELOPE_FROM_DEFINITION_ID),
        [GET_ENVELOPE_FROM_ID]: getNamespacedResource(PROJECT_NAMESPACE, GET_ENVELOPE_FROM_ID),
        [EVALUATED_FIELD_FROM_ID]: getNamespacedResource(PROJECT_NAMESPACE, EVALUATED_FIELD_FROM_ID),
        [IS_ATTACHMENT_ID_PART_OF_ENVELOPE]: getNamespacedResource(PROJECT_NAMESPACE, IS_ATTACHMENT_ID_PART_OF_ENVELOPE)
    },
    mutations: {
        [COMMIT_DATA]: getNamespacedResource(PROJECT_NAMESPACE, COMMIT_DATA),
        [COMMIT_MULTIDATA]: getNamespacedResource(PROJECT_NAMESPACE, COMMIT_MULTIDATA),
        [COMMIT_MULTIDATA_ITEM]: getNamespacedResource(PROJECT_NAMESPACE, COMMIT_MULTIDATA_ITEM),
        [COMMIT_EVALUATED_FIELD]: getNamespacedResource(PROJECT_NAMESPACE, COMMIT_EVALUATED_FIELD),
        [COMMIT_ATTACHMENT]: getNamespacedResource(PROJECT_NAMESPACE, COMMIT_ATTACHMENT),
        [COMMIT_ENVELOPE]: getNamespacedResource(PROJECT_NAMESPACE, COMMIT_ENVELOPE),
        [HIDE_ATTACHMENT_BY_DEFINITION_ID]: getNamespacedResource(PROJECT_NAMESPACE, HIDE_ATTACHMENT_BY_DEFINITION_ID),
        [HIDE_ENVELOPE]: getNamespacedResource(PROJECT_NAMESPACE, HIDE_ENVELOPE)
    },
    actions: {
        [REMOVE_ENVELOPE]: getNamespacedResource(PROJECT_NAMESPACE, REMOVE_ENVELOPE)
    }
};
export { PROJECT_NAMESPACE, PROJECT_TYPES };

export function createProjectModule(state) {
    state.evaluatedFields = {};
    const projectLockStore = useProjectLockStore();

    return {
        namespaced: true,
        state: state,
        getters: {
            [DATA_FIELD_VALUE_FROM_ID](state) {
                return function (dataFieldId) {
                    return state.DataFields[dataFieldId].Data;
                };
            },
            [DATA_FIELD_MULTIVALUE_FROM_ID](state) {
                return function (dataFieldId) {
                    return state.DataFields[dataFieldId].MultiInstanceData;
                };
            },
            [ATTACHMENT_FROM_ID](state) {
                return function (attachmentDefinitionId) {
                    return state.Attachments[attachmentDefinitionId];
                };
            },
            [GET_ATTACHMENT_DEFINITION_ID_FROM_ATTACHMENT_ID](state) {
                return function (attachmentId) {
                    const attachments = Object.values(state.Attachments);
                    const foundAttachment = attachments.find((a) => a?.AttachmentId === attachmentId);
                    return foundAttachment?.AttachmentDefinitionId;
                };
            },
            [GET_ENVELOPE_FROM_DEFINITION_ID](state) {
                return function (envelopeDefinitionId) {
                    return state.Envelopes[envelopeDefinitionId];
                };
            },
            [GET_ENVELOPE_FROM_ID](state) {
                return function (envelopeId) {
                    return Object.values(state.Envelopes).find((e) => e?.EnvelopeId === envelopeId);
                };
            },
            [IS_ATTACHMENT_ID_PART_OF_ENVELOPE](state) {
                return function (attachmentId) {
                    for (const envelope of Object.values(state.Envelopes)) {
                        if (envelope == null) {
                            continue;
                        }

                        if (envelope.TargetAttachmentId === attachmentId) {
                            return true;
                        }
                        if (envelope.CertificateTargetAttachmentId === attachmentId) {
                            return true;
                        }

                        for (const item of Object.values(envelope.Items)) {
                            if (item.TargetAttachmentId === attachmentId) {
                                return true;
                            }
                            if (item.AttachmentId === attachmentId) {
                                return true;
                            }
                        }
                    }
                    return false;
                };
            },
            [EVALUATED_FIELD_FROM_ID](state) {
                return function (calculatedFieldId) {
                    return state.evaluatedFields[calculatedFieldId];
                };
            }
        },
        mutations: {
            [COMMIT_DATA](state, { dataFieldId, value }) {
                if (projectLockStore.isLocked) {
                    return;
                }
                value = convertValueToString(value);
                if (state.DataFields[dataFieldId]) {
                    state.DataFields[dataFieldId].Data = value;
                }
            },
            [COMMIT_MULTIDATA](state, { dataFieldId, value }) {
                if (projectLockStore.isLocked) {
                    return;
                }
                if (state.DataFields[dataFieldId]) {
                    state.DataFields[dataFieldId].MultiInstanceData = value;
                }
            },
            [COMMIT_MULTIDATA_ITEM](state, { dataFieldId, index, value }) {
                if (projectLockStore.isLocked) {
                    return;
                }
                if (state.DataFields[dataFieldId]) {
                    // Need to backfill other items
                    for (let i = 0; i < index; i++) {
                        if (state.DataFields[dataFieldId].MultiInstanceData == undefined) {
                            state.DataFields[dataFieldId].MultiInstanceData[i] = "";
                        }
                    }
                    state.DataFields[dataFieldId].MultiInstanceData[index] = value;
                }
            },
            [COMMIT_EVALUATED_FIELD](state, { evaluatedField }) {
                if (projectLockStore.isLocked) {
                    return;
                }
                if (evaluatedField) {
                    state.evaluatedFields[evaluatedField.PublicId] = evaluatedField;
                }
            },
            [COMMIT_ATTACHMENT](state, { attachmentId, value }) {
                if (projectLockStore.isLocked) {
                    return;
                }
                if (state.Attachments.hasOwnProperty(attachmentId)) {
                    state.Attachments[attachmentId] = value;
                }
            },
            [COMMIT_ENVELOPE](state, { envelopeDefinitionId, value }) {
                if (projectLockStore.isLocked) {
                    return;
                }
                if (state.Envelopes.hasOwnProperty(envelopeDefinitionId)) {
                    state.Envelopes[envelopeDefinitionId] = value;
                }
            },
            [HIDE_ATTACHMENT_BY_DEFINITION_ID](state, { attachmentDefinitionId }) {
                state.Attachments[attachmentDefinitionId] = null;
            },
            [HIDE_ENVELOPE](state, { envelopeDefinitionId }) {
                state.Envelopes[envelopeDefinitionId] = null;
            }
        },
        actions: {
            [REMOVE_ENVELOPE]({ getters, commit }, { envelopeId }) {
                const envelope = getters[GET_ENVELOPE_FROM_ID](envelopeId);
                const envelopeDefinitionId = envelope.Definition.PublicId;

                function removeAttachmentByAttachmentId(attachmentId) {
                    const attachmentDefinitionId =
                        getters[GET_ATTACHMENT_DEFINITION_ID_FROM_ATTACHMENT_ID](attachmentId);
                    if (attachmentDefinitionId) {
                        commit(HIDE_ATTACHMENT_BY_DEFINITION_ID, { attachmentDefinitionId: attachmentDefinitionId });
                    }
                }

                for (const item of Object.values(envelope.Items).filter((i) => i.TargetAttachmentId)) {
                    removeAttachmentByAttachmentId(item.TargetAttachmentId);
                }
                if (envelope.CertificateTargetAttachmentId) {
                    removeAttachmentByAttachmentId(envelope.CertificateTargetAttachmentId);
                }
                if (envelope.TargetAttachmentId) {
                    removeAttachmentByAttachmentId(envelope.TargetAttachmentId);
                }
                commit(HIDE_ENVELOPE, { envelopeDefinitionId: envelopeDefinitionId });
            }
        }
    };
}
